import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare, CreditCard, CalendarBlank, ChatCircleDots, FileText, ChartLine,
  Bandaids, Phone, User, Sparkle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const CARDS = [
  { to: "#", label: "Attendance",           icon: CheckSquare,   desc: "Your child's session attendance" },
  { to: "#", label: "Payments",             icon: CreditCard,    desc: "Invoices & fees" },
  { to: "#", label: "Competition Calendar", icon: CalendarBlank, desc: "Upcoming meets & fixtures" },
  { to: "#", label: "Coach Messages",       icon: ChatCircleDots,desc: "Direct message the coach" },
  { to: "#", label: "Reports",              icon: FileText,      desc: "Progress & performance reports" },
  { to: "#", label: "Assessment Results",   icon: ChartLine,     desc: "Latest test scores" },
  { to: "#", label: "Medical Forms",        icon: Bandaids,      desc: "Consent & waivers" },
  { to: "#", label: "Emergency Contacts",   icon: Phone,         desc: "Update on-file contacts" },
];

export default function ParentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/parent/dashboard").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  const linked = data?.linked_athletes || [];
  const isLinked = linked.length > 0;

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="parent-dashboard">
      <div className="mb-8">
        <div className="label-tiny text-amber-300 mb-2">// PARENT / GUARDIAN</div>
        <h1 className="font-heading text-4xl sm:text-5xl">FAMILY HUB</h1>
        <p className="text-text-secondary text-sm mt-2">
          {user?.name?.split(" ")[0] || "Parent"} · Track your athlete&apos;s progress, attendance and payments.
        </p>
      </div>

      {/* Linked athletes strip */}
      <div className="border border-amber-400/30 bg-amber-400/[0.04] p-4 mb-6" data-testid="parent-linked-strip">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="label-tiny text-amber-300 mb-1">// LINKED ATHLETES ({linked.length})</div>
            {isLinked ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {linked.map((a) => (
                  <div key={a.user_id} className="inline-flex items-center gap-2 border border-amber-400/40 px-3 py-1" data-testid={`parent-athlete-${a.user_id}`}>
                    <User size={14} className="text-amber-300" />
                    <span className="text-sm font-heading">{a.name || "—"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-text-secondary mt-1">
                <Sparkle size={11} weight="fill" className="inline mr-1 text-amber-300" />
                No athlete linked yet. Ask your child&apos;s coach for a family invite (arriving in R5).
              </div>
            )}
          </div>
        </div>
      </div>

      {isLinked && data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 mb-6" data-testid="parent-kpis">
          <div className="bg-surface-DEFAULT p-4" data-testid="parent-kpi-attendance">
            <div className="label-tiny text-[9px] text-text-secondary mb-1">ATTENDANCE 30D</div>
            <div className="font-heading text-2xl text-amber-300 tabular-nums">{data.counts?.attendance_30d ?? 0}</div>
          </div>
          <div className="bg-surface-DEFAULT p-4" data-testid="parent-kpi-competitions">
            <div className="label-tiny text-[9px] text-text-secondary mb-1">UPCOMING MEETS</div>
            <div className="font-heading text-2xl text-amber-300 tabular-nums">{data.counts?.competitions_upcoming ?? 0}</div>
          </div>
          <div className="bg-surface-DEFAULT p-4" data-testid="parent-kpi-tests">
            <div className="label-tiny text-[9px] text-text-secondary mb-1">RECENT TESTS</div>
            <div className="font-heading text-2xl text-amber-300 tabular-nums">{data.counts?.tests_recent ?? 0}</div>
          </div>
        </div>
      )}

      {isLinked && data?.recent_attendance?.length > 0 && (
        <div className="border border-slate-200 p-4 mb-6" data-testid="parent-attendance-list">
          <div className="label-tiny mb-3">// RECENT ATTENDANCE ({data.recent_attendance.length})</div>
          <div className="space-y-1 text-xs">
            {data.recent_attendance.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <div className="truncate">
                  <span className="text-white">{a.athlete_name}</span>
                  <span className="text-text-secondary"> · {a.name || "Session"}</span>
                </div>
                <span className="text-amber-300 tabular-nums">{new Date(a.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10" data-testid="parent-quickactions">
        {CARDS.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            data-testid={`parent-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`bg-surface-DEFAULT ${isLinked ? "hover:bg-surface-elevated" : "opacity-60"} p-5 group transition`}
          >
            <c.icon size={20} weight="duotone" className="text-amber-300 mb-3" />
            <div className="font-heading text-lg mb-1">{c.label.toUpperCase()}</div>
            <div className="text-xs text-text-secondary">{c.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
